import React, { useMemo, useCallback, useState } from 'react';
import { Flex } from '@chakra-ui/react';

export type ItemActions = {
  selectItem: () => void;
};

export type ListActions<ItemId extends string> = {
  toggleSidebar: () => void;
  selectItem: (id: ItemId) => void;
  nextPage?: () => void;
  previousPage?: () => void;
};

// When the sidebar is hidden on mobile, the following actions are used
// to show the sidebar as a modal.
// When not on mobile, these actions do nothing.
export type ModalSidebarActions = {
  showModalSidebar: () => void;
  hideModalSidebar: () => void;
};

export type ListItems<Item, ItemId extends string> = ({ id: ItemId; data?: Item } & ItemActions)[];
export type ListProps<Item, ItemId extends string> = {
  selectedId?: ItemId;
  items: ListItems<Item, ItemId>;
  // ListDetail is condensed when the sidebar is hidden on mobile
  isCondensed: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  isLoading?: boolean;
  isError?: boolean;

  actions: ListActions<ItemId> & ModalSidebarActions;
};
export type DetailProps<Item, ItemId extends string> = {
  id: ItemId;
  item?: { id: ItemId; data?: Item };
  // ListDetail is condensed when the sidebar is hidden on mobile
  isCondensed: boolean;
  isSelected: boolean;
  actions: ModalSidebarActions;
};

export interface ListDetailProps<Item, ItemId extends string, Items> {
  List: React.ComponentType<ListProps<Item, ItemId>>;
  Detail: React.ComponentType<DetailProps<Item, ItemId>>;
  items: Items;
  selectedId?: ItemId;

  isCondensed: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  isLoading?: boolean;
  isError?: boolean;

  onSelect?: (_id: ItemId) => void;
  nextPage?: () => void;
  previousPage?: () => void;

  // width used when the sidebar is not condensed
  sidebarWidth?: string;
}

export function ListDetail<Item, ItemId extends string, Items extends Map<ItemId, Item>>(
  props: ListDetailProps<Item, ItemId, Items>,
) {
  const {
    List,
    Detail,
    selectedId,
    onSelect,
    nextPage,
    previousPage,
    isCondensed,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    isError,
  } = props;
  const [selectedIdSet, setSelectedIdSet] = useState<Set<ItemId>>(
    new Set(props.selectedId ? [props.selectedId] : []),
  );
  // show the sidebar by default when there is no selected item
  // or when the component is not condensed
  const [showSidebar, setShowSidebar] = useState<boolean>(
    typeof props.selectedId === 'undefined' || !props.isCondensed,
  );

  const updateSelectedIdSet = useCallback(
    (id: ItemId) => {
      setSelectedIdSet((idSet) => {
        if (idSet.has(id)) {
          return idSet;
        }
        if (idSet.size > 5) {
          const ids = [...idSet.values()];
          ids.shift();
          return new Set([...ids, id]);
        }
        return new Set([...idSet, id]);
      });
    },
    [setSelectedIdSet],
  );

  const selectId = useCallback(
    (id: ItemId) => {
      if (onSelect) {
        onSelect(id);
      }

      updateSelectedIdSet(id);
    },
    [onSelect, updateSelectedIdSet],
  );

  const toggleSidebar = useCallback(() => {
    return setShowSidebar(!showSidebar);
  }, [showSidebar, setShowSidebar]);

  const listProps = {
    items: props.items,
    isCondensed,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    isError,
  };

  const detailItem = useMemo(() => {
    if (!selectedId) {
      return undefined;
    }

    // make sure the selectedId is in the selectedIdSet
    updateSelectedIdSet(selectedId);

    return {
      id: selectedId,
      data: props.items.get(selectedId),
    };
  }, [props.items, selectedId, updateSelectedIdSet]);

  const handleOnSelect = useCallback(
    (id: ItemId) => {
      if (typeof onSelect === 'function') {
        onSelect?.(id);
      }
      selectId(id);
    },
    [onSelect, selectId],
  );

  const items = useMemo(() => {
    return Array.from(props.items.entries()).map(([id, item]) => ({
      data: item,
      id,
      selectItem: () => {
        handleOnSelect(id);
        if (isCondensed) {
          setShowSidebar(false);
        }
      },
    }));
  }, [props.items, isCondensed, handleOnSelect]);

  const listActions = useMemo(() => {
    return {
      toggleSidebar,
      nextPage,
      previousPage,
      selectItem: handleOnSelect,
      showModalSidebar: () => {
        if (isCondensed) {
          setShowSidebar(true);
        }
      },
      hideModalSidebar: () => {
        if (isCondensed) {
          setShowSidebar(false);
        }
      },
    };
  }, [toggleSidebar, nextPage, previousPage, handleOnSelect, isCondensed]);

  const detailActions = useMemo(() => {
    if (!isCondensed) {
      setShowSidebar(true);

      return {
        showModalSidebar: () => {},
        hideModalSidebar: () => {},
      };
    }

    // when we're in condensed mode, we only show the sidebar on init when
    // we're not showing a detail item
    setShowSidebar(typeof selectedId === 'undefined');

    return {
      showModalSidebar: () => {
        if (isCondensed) {
          setShowSidebar(true);
        }
      },
      hideModalSidebar: () => {
        if (isCondensed) {
          setShowSidebar(false);
        }
      },
    };
  }, [isCondensed, selectedId]);
  // when condensed and we're showing the sidebar we hide the Detail
  // importantly we don't unmount it, as we want to keep the state and any
  // created resources (like a websocket connection) in case the user
  // goes back into the same item

  return (
    <Flex height="100%" width="100%" flexDir="row" overflow="auto">
      <Sidebar showSidebar={showSidebar} fullWidth={isCondensed} width={props.sidebarWidth}>
        <List {...listProps} selectedId={selectedId} items={items} actions={listActions} />
      </Sidebar>
      <Flex
        key={selectedId}
        display={isCondensed && showSidebar ? 'none' : 'flex'}
        height="100%"
        width="100%"
        overflow="auto"
      >
        {selectedId
          ? [...selectedIdSet.values()].map((id) => (
              <Detail
                key={id}
                id={id}
                isSelected={id === selectedId}
                item={detailItem}
                isCondensed={isCondensed}
                actions={detailActions}
              />
            ))
          : null}
      </Flex>
    </Flex>
  );
}

export function Sidebar({
  showSidebar,
  fullWidth,
  width,
  children,
}: {
  showSidebar: boolean;
  fullWidth: boolean;
  width?: string;
  children: React.ReactNode;
}) {
  const showProps = useMemo(() => {
    if (!showSidebar) {
      return {
        borderRightWidth: 0,
        width: 0,
        display: 'none',
      };
    }

    if (fullWidth) {
      return {
        borderRightWidth: '0px',
        width: '100%',
        display: 'flex',
      };
    }

    return {
      borderRightWidth: '1px',
      width: width || '24rem',
      display: 'flex',
    };
  }, [showSidebar, fullWidth, width]);

  return (
    <Flex
      bg="bg.canvas"
      {...showProps}
      direction="column"
      alignItems="flex-start"
      justifyContent="stretch"
      flexShrink="0"
      overflow="auto"
      h="100%"
    >
      {children}
    </Flex>
  );
}
